<template>
    <div class="px-1">
        <VcModal
            :visible="creationMode"
            :width="dialogWidth"
            @close="creationMode = false"
        >
            <div class="pb-4">
                <RedesignHtmlEditor
                    v-if="creationMode"
                    @save="addNote"
                    @cancel="creationMode = false"
                />
            </div>
        </VcModal>
        <div
            v-for="(note, index) in fetchNotes"
            :key="index"
            class="flex flex-row justify-between"
        >
            <div class="border p-2 w-48 text-center">
                {{ getFormattedDate(note.date) }}
            </div>
            <div class="border p-2 flex-1">
                <div class="text-main-sm">
                    <RedesignHtmlEditor
                        v-if="noteToUpdate === note.date"
                        :model-value="note.text"
                        @save="(data: string) => updateNote(data, note)"
                        @cancel="noteToUpdate = null"
                    />
                    <div
                        v-else
                        v-html="note.text"
                    />
                </div>
            </div>
            <div
                v-if="showActions(note)"
                class="border p-2 w-32"
            >
                <div class="flex justify-center">
                    <VcPopover
                        trigger="hover"
                        placement="top"
                    >
                        <template #reference>
                            <EyeOff
                                v-if="!note.displayed"
                                class="text-primary w-4 cursor-pointer"
                                @click="displayNoteInList(true, note)"
                            />
                        </template>
                        <div class="p-2">{{ t('widget.editor.displayNoteOnTheList') }}</div>
                    </VcPopover>
                    <VcPopover
                        trigger="hover"
                        placement="top"
                    >
                        <template #reference>
                            <Eye
                                v-if="note.displayed"
                                class="text-primary w-4 cursor-pointer"
                                @click="displayNoteInList(false, note)"
                            />
                        </template>
                        <div class="p-2">{{ t('widget.editor.hideNoteFromList') }}</div>
                    </VcPopover>
                    <VcPopover
                        trigger="hover"
                        placement="top"
                    >
                        <template #reference>
                            <Pencil
                                class="text-green w-4 mx-2 cursor-pointer"
                                @click="onEditNote(note)"
                            />
                        </template>
                        <div class="p-2">{{ t('widget.editor.openEditModeForThisNote') }}</div>
                    </VcPopover>
                    <VcPopover
                        trigger="hover"
                        placement="top"
                    >
                        <template #reference>
                            <Trash2
                                class="text-red w-4 cursor-pointer"
                                @click="removeNote(note)"
                            />
                        </template>
                        <div class="p-2">{{ t('widget.editor.removeNote') }}</div>
                    </VcPopover>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import get from 'lodash/get'
import { Eye, EyeOff, Pencil, Trash2 } from 'lucide-vue-next'
import { computed, defineAsyncComponent, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { VcModal, VcPopover } from '@voicenter-team/voicenter-ui-plus'
import useConfirmModal from '@/composables/useConfirmModal'
import type { Note, WidgetExtended } from '@/types/dashboard'
import { dayjs } from '@/composables/useDayjs'

const RedesignHtmlEditor = defineAsyncComponent(() => import('@/components/RedesignBaseComponents/RedesignHtmlEditor.vue'))

const { t } = useI18n()

const props = withDefaults(
    defineProps<{
        data: WidgetExtended
        editable?: boolean
        onEditMode?: boolean
    }>(),
    {
        editable: false,
        onEditMode: false,
    }
)

const emit = defineEmits<{ (e: 'on-update', data: WidgetExtended): void }>()

const creationMode = ref(false)
const editMode = ref(false)
const noteToUpdate = ref<number | null>(null)
const dialogWidth = '90%'

const fetchNotes = computed(() => {
    const allNotes = get(props.data?.WidgetLayout, 'Notes', []) ?? []
    if (editMode.value) {
        return allNotes
    }
    return allNotes.filter((note: Note) => note.displayed)
})

function getFormattedDate (date: number) {
    return dayjs(date).format('YY-MM-DD HH:mm')
}

function onAddNote () {
    creationMode.value = !creationMode.value
}

function onEditNote (note: Note) {
    noteToUpdate.value = note.date
}

function newNoteObject (): Note {
    return {
        date: new Date().getTime(),
        text: '',
        displayed: true,
    }
}

function getNoteIndex (note: Note) {
    return (props.data?.WidgetLayout?.Notes ?? []).findIndex((el) => el.date === note.date)
}

function emmitUpdate () {
    if (props.data?.WidgetLayout) {
        props.data.WidgetLayout.editMode = editMode.value
    }
    emit('on-update', props.data)
    noteToUpdate.value = null
}

function addNote (val: string) {
    creationMode.value = false
    if (val && props.data?.WidgetLayout) {
        const note = newNoteObject()
        note.text = val
        if (!props.data.WidgetLayout.Notes) {
            props.data.WidgetLayout.Notes = []
        }
        props.data.WidgetLayout.Notes.push(note)
        emmitUpdate()
    }
}

function removeNote (note: Note) {
    useConfirmModal({
        header: String(t('widget.editor.removeNote')),
        message: String(t('common.confirm.question', { action: t('widget.editor.toRemoveThisNote') })),
        type: 'delete',
    }).then((isConfirmed) => {
        if (isConfirmed && props.data?.WidgetLayout?.Notes) {
            const noteIndex = getNoteIndex(note)
            props.data.WidgetLayout.Notes.splice(noteIndex, 1)
            emmitUpdate()
        }
    })
}

function displayNoteInList (state: boolean, note: Note) {
    note.displayed = state
    fetchAndUpdate(note)
}

function updateNote (text: string, note: Note) {
    note.text = text
    fetchAndUpdate(note)
}

function fetchAndUpdate (note: Note) {
    if (props.data?.WidgetLayout?.Notes) {
        const noteIndex = getNoteIndex(note)
        props.data.WidgetLayout.Notes[noteIndex] = note
    }
    emmitUpdate()
}

function showActions (note: Note) {
    return editMode.value && (noteToUpdate.value == null || noteToUpdate.value !== note.date)
}

onMounted(() => {
    if (props.data?.WidgetLayout && !('Notes' in props.data.WidgetLayout)) {
        props.data.WidgetLayout.Notes = []
    }
})

watch(
    () => props.onEditMode,
    (state) => {
        editMode.value = state ?? false
    },
    { immediate: true }
)

defineExpose({
    onAddNote
})
</script>
